import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { api, type Reader } from '@/api/client';
import { useCellCommentShellOptional } from '@/features/project-cell-comments/cellCommentShellContext';
import { useClickOutside } from '@/hooks/useClickOutside';
import { useOverlayAnimation } from '@/hooks/useOverlayAnimation';
import { usePopoverPositioning } from '@/hooks/usePopoverPositioning';
import { EmailInput } from '@/components/ui/EmailInput/EmailInput';
import { PhoneInput } from '@/components/ui/PhoneInput/PhoneInput';
import { Button } from '@/components/ui/Button/Button';
import { ContextMenu } from '@/components/ui/ContextMenu/ContextMenu';
import type { ContextMenuItem } from '@/components/ui/ContextMenu/ContextMenu';
import { useToast } from '@/contexts/ToastContext';
import { OverlayScrollArea } from '@/components/ui/OverlayScrollArea/OverlayScrollArea';
import { OverlayIntrinsicSize } from '@/components/ui/OverlayIntrinsicSize/OverlayIntrinsicSize';
import { formatRuPhoneDisplay, ruPhoneToE164 } from '@/utils/phoneRu';
import {
  patchReaderInTableCache,
  invalidateReaderCachesExceptTable,
} from '@/lib/readersCatalogQueryCache';
import { EditableCellPopoverHeader } from '@/components/ProjectsTable/EditableCellPopoverHeader';
import '@/components/ProjectsTable/EditableCell.css';

const CONTACTS_POPOVER_WIDTH = 440;

function trimOrNull(value: string | null | undefined): string | null {
  const t = value?.trim();
  return t ? t : null;
}

export interface ReaderContactsComboCellProps {
  reader: Reader;
  disabled?: boolean;
}

function contactsComboDisplay(reader: Reader): ReactNode {
  const phoneRaw = trimOrNull(reader.phone);
  const phone = phoneRaw ? formatRuPhoneDisplay(phoneRaw) || phoneRaw : null;
  const email = trimOrNull(reader.email);
  const lines: { key: string; label: string; value: string }[] = [];
  if (phone) lines.push({ key: 'phone', label: 'Телефон', value: phone });
  if (email) lines.push({ key: 'email', label: 'Email', value: email });

  if (lines.length === 0) {
    return (
      <div className="projects-table__combo-cell projects-table__combo-cell_empty">
        <span className="projects-table__empty-value">N/A</span>
      </div>
    );
  }

  return (
    <div className="projects-table__combo-cell projects-table__labeled-cell projects-table__labeled-cell_has-content">
      {lines.map(({ key, label, value }) => (
        <span key={key} className="projects-table__combo-line">
          <span className="projects-table__labeled-cell-label">{label}:</span> {value}
        </span>
      ))}
    </div>
  );
}

/** Комбо «Телефон» + «Email»: отображение как в проектах; ПКМ → редактирование через PATCH readers. */
export function ReaderContactsComboCell({ reader, disabled = false }: ReaderContactsComboCellProps) {
  const cellCommentShell = useCellCommentShellOptional();
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [contextMenuOpen, setContextMenuOpen] = useState(false);
  const [contextMenuPos, setContextMenuPos] = useState<{ x: number; y: number } | null>(null);
  const [error, setError] = useState('');
  const [phoneDraft, setPhoneDraft] = useState('');
  const [emailDraft, setEmailDraft] = useState('');

  const cellRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const positionAnchorRef = useRef<HTMLDivElement | null>(null);
  if (popoverOpen && cellRef.current) positionAnchorRef.current = cellRef.current;
  const triggerRef = useRef<HTMLDivElement | null>(null);
  triggerRef.current = popoverOpen ? cellRef.current : positionAnchorRef.current;

  const { shouldRender, contentClasses, requestClose: requestOverlayClose, isExiting } = useOverlayAnimation(
    popoverOpen,
    useCallback(() => {
      setPopoverOpen(false);
      setError('');
    }, []),
    true
  );

  usePopoverPositioning({
    triggerRef: triggerRef as RefObject<HTMLElement | null>,
    popoverRef,
    isOpen: shouldRender,
    preferredPositionY: 'bottom',
    preferredPositionX: 'left',
    offset: 8,
    zIndex: 1000,
    onAnchorDetached: requestOverlayClose,
    dependencies: [],
  });

  useClickOutside(popoverRef, requestOverlayClose as (e: MouseEvent) => void, {
    excludeRefs: [],
    excludeSelector: '[data-combo-dropdown]',
    enabled: shouldRender,
    capture: false,
  });

  const updateMutation = useMutation({
    mutationFn: (data: Parameters<typeof api.readers.update>[1]) => api.readers.update(reader.id, data),
    onSuccess: async (updated: Reader) => {
      showSuccess('Данные сохранены');
      patchReaderInTableCache(queryClient, updated);
      await invalidateReaderCachesExceptTable(queryClient);
      requestOverlayClose();
    },
    onError: (err: Error) => {
      showError(err.message);
      setError(err.message);
    },
  });

  useEffect(() => {
    if (!popoverOpen) return;
    setError('');
    const p = reader.phone?.trim() ?? '';
    setPhoneDraft(p ? formatRuPhoneDisplay(p) : '');
    setEmailDraft(reader.email?.trim() ?? '');
  }, [popoverOpen, reader.phone, reader.email]);

  const openEditPopover = useCallback(() => {
    if (disabled) return;
    setPopoverOpen(true);
  }, [disabled]);

  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      if (disabled) return;
      e.preventDefault();
      e.stopPropagation();
      setContextMenuPos({ x: e.clientX, y: e.clientY });
      setContextMenuOpen(true);
    },
    [disabled]
  );

  const handleEditFromMenu = useCallback(() => {
    setContextMenuOpen(false);
    openEditPopover();
  }, [openEditPopover]);

  const contextMenuItems = useMemo(() => {
    const items: ContextMenuItem[] = [{ label: 'Редактировать', onClick: handleEditFromMenu }];
    if (cellCommentShell) items.push(cellCommentShell.addCommentMenuItem);
    return items;
  }, [cellCommentShell, handleEditFromMenu]);

  const handleSave = useCallback(() => {
    setError('');
    const emailTrim = emailDraft.trim();
    const phoneTrim = phoneDraft.trim();

    if (emailTrim && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim)) {
      setError('Введите корректный email или очистите поле');
      return;
    }

    let phonePayload: string | null = null;
    if (phoneTrim) {
      const e164 = ruPhoneToE164(phoneDraft);
      if (!e164) {
        setError('Введите полный номер в формате РФ или очистите поле');
        return;
      }
      phonePayload = e164;
    }

    updateMutation.mutate({
      email: emailTrim ? emailTrim : null,
      phone: phonePayload,
    });
  }, [emailDraft, phoneDraft, updateMutation]);

  const isPending = updateMutation.isPending;
  const hasAnchor = !!triggerRef.current;
  const popoverAnchorStyle: CSSProperties = {
    position: 'fixed',
    pointerEvents: isExiting ? 'none' : 'auto',
    left: hasAnchor ? -9999 : 8,
    top: hasAnchor ? -9999 : 8,
  };
  const popoverPanelStyle: CSSProperties = {
    width: CONTACTS_POPOVER_WIDTH,
    minWidth: CONTACTS_POPOVER_WIDTH,
    maxWidth: 'calc(100vw - 16px)',
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: 'var(--overlay-bg)',
    backdropFilter: 'var(--overlay-blur)',
    WebkitBackdropFilter: 'var(--overlay-blur)',
    border: 'var(--overlay-border)',
    borderRadius: 'var(--overlay-radius)',
    boxShadow: 'var(--overlay-shadow)',
  };

  const display = useMemo(() => contactsComboDisplay(reader), [reader]);

  return (
    <>
      <div
        ref={cellRef}
        className={clsx('editable-cell', disabled && 'readers-table-catalog__editable-cell_disabled')}
        onContextMenu={handleContextMenu}
      >
        <div className="editable-cell__content">{display}</div>
        <ContextMenu
          isOpen={contextMenuOpen}
          onClose={() => setContextMenuOpen(false)}
          anchorPosition={contextMenuPos}
          items={contextMenuItems}
        />
      </div>
      {shouldRender &&
        createPortal(
          <div ref={popoverRef} style={popoverAnchorStyle}>
            <OverlayIntrinsicSize>
              <div style={popoverPanelStyle} className={clsx('editable-cell-popover', contentClasses)}>
                <EditableCellPopoverHeader onClose={requestOverlayClose}>Контакты</EditableCellPopoverHeader>
                <OverlayScrollArea className="editable-cell-popover__form editable-cell-popover__form_has-overlay-scroll">
                  <div className="editable-cell-popover__form-stack">
                    <PhoneInput value={phoneDraft} onChange={setPhoneDraft} disabled={isPending || disabled} fullWidth />
                    <EmailInput value={emailDraft} onChange={setEmailDraft} disabled={isPending || disabled} fullWidth />
                    {error ? (
                      <span className="form__error" role="alert">
                        {error}
                      </span>
                    ) : null}
                  </div>
                </OverlayScrollArea>
                <div className="editable-cell-popover__footer">
                  <Button type="button" variant="primary" onClick={handleSave} disabled={isPending || disabled}>
                    {isPending ? 'Сохранение…' : 'Сохранить'}
                  </Button>
                </div>
              </div>
            </OverlayIntrinsicSize>
          </div>,
          document.body
        )}
    </>
  );
}

ReaderContactsComboCell.displayName = 'ReaderContactsComboCell';
